import isNil from 'lodash/isNil';
import type { FormValues } from '../../../../common/types';
import {
	DAYS_OF_THE_WEEK,
	TIME_PICKER_FIELD_PREFIX,
	TIME_PICKER_FIELD_START,
	TIME_PICKER_FIELD_END,
	HOLIDAY_PREFIX,
} from '../../../../common/ui/constants.tsx';
import type { HolidayRequest } from '../../../../services/save-calendar/types.tsx';
import type { WorkingTimes, WorkingTime } from './types';

const mapToId = (value: string): string => {
	const id = value.split('-').pop();
	// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
	return id;
};

const toMilliseconds = (value: string) => {
	// value is time in hh:mm format. Don't need a date-time utility to convert that to ms
	const [hours, minutes] = value.split(':');
	return (parseInt(hours, 10) * 3600 + parseInt(minutes, 10) * 60) * 1000;
};

const filterAndMapToId = (values: string[], fieldPrefix: string): string[] =>
	values.filter((key) => key.startsWith(fieldPrefix)).map(mapToId);

const filterMatchingWeekdays = (
	workingTimes: WorkingTimes,
	workingTimeToBeAdded: WorkingTime,
): WorkingTimes =>
	workingTimes.filter(
		({ weekday, start, end }) =>
			weekday === workingTimeToBeAdded.weekday &&
			workingTimeToBeAdded.end >= start &&
			workingTimeToBeAdded.start <= end,
	);

// time pickers usually have human readable values i.e in HH:MM
// this utils will extract the selected day & connected time pickers and normalize to milliseconds
// result will be sorted based on start times
const mapSelectedStartAndEndTimesToMilliSeconds = (
	formKeys: string[],
	formData: FormValues,
): WorkingTimes => {
	const selectedDays = DAYS_OF_THE_WEEK.filter((day) => formData[day]);
	return selectedDays // eslint-disable-next-line @typescript-eslint/no-explicit-any
		.reduce<Array<any>>((workingTimes, selectedDay) => {
			const connectedTimePickerIds = filterAndMapToId(
				formKeys,
				`${TIME_PICKER_FIELD_PREFIX}${selectedDay}${TIME_PICKER_FIELD_START}`,
			);

			connectedTimePickerIds.forEach((timePickerId) => {
				const start =
					formData[
						`${TIME_PICKER_FIELD_PREFIX}${selectedDay}${TIME_PICKER_FIELD_START}${timePickerId}`
					];
				const end =
					formData[
						`${TIME_PICKER_FIELD_PREFIX}${selectedDay}${TIME_PICKER_FIELD_END}${timePickerId}`
					];

				workingTimes.push({
					weekday: selectedDay,
					start: toMilliseconds(start),
					end: toMilliseconds(end),
				});
			});
			return workingTimes;
		}, [])
		.sort((first, second) => first.start - second.start);
};

// mergeIntervalsByDay utils will try to merge working times
// e.g: 9AM-17PM and 17PM-20PM will be merged to 9AM-20PM
const mergeIntervalsByDay = (
	workingTimes: WorkingTimes,
	workingTimeToBeAdded: WorkingTime,
): WorkingTimes => {
	const [mergeableWorkingTime] = filterMatchingWeekdays(workingTimes, workingTimeToBeAdded);

	if (isNil(mergeableWorkingTime)) {
		workingTimes.push(workingTimeToBeAdded);
	} else {
		mergeableWorkingTime.start =
			mergeableWorkingTime.start < workingTimeToBeAdded.start
				? mergeableWorkingTime.start
				: workingTimeToBeAdded.start;
		mergeableWorkingTime.end =
			mergeableWorkingTime.end > workingTimeToBeAdded.end
				? mergeableWorkingTime.end
				: workingTimeToBeAdded.end;
	}

	return workingTimes;
};

// getWorkingTimes utils exact the start and end times by day in milliseconds
// final result includes merged intervals
export const getWorkingTimes = (formKeys: string[], formData: FormValues): WorkingTimes => {
	const selectedDays = mapSelectedStartAndEndTimesToMilliSeconds(formKeys, formData);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return selectedDays.reduce<Array<any>>(
		(workingTimes: WorkingTimes, workingTimeToBeAdded: WorkingTime) =>
			mergeIntervalsByDay(workingTimes, workingTimeToBeAdded),
		[],
	);
};

export const getHolidays = (formKeys: string[], formData: FormValues) => {
	const holidayIds = filterAndMapToId(formKeys, `${HOLIDAY_PREFIX}`);
	return holidayIds.map<HolidayRequest>((id) => {
		const { defaultDate, defaultName, isRecurring } = formData[`${HOLIDAY_PREFIX}${id}`];
		return {
			name: defaultName,
			iso8601Date: defaultDate,
			recurring: isRecurring,
		};
	});
};
